import { useInstitution } from '#core/Institution';
import Heading from '#coreComponents/Heading';
import { SIDEBAR_WIDTH } from '#coreComponents/Sidebar/constants';
import InstitutionLogo from '#coreComponents/InstitutionLogo/InstitutionLogo';
import { CaretSortUp20, CheckmarkFilled16 } from '@carbon/icons-react';
import { Box, BoxProps, Flex, HStack, Icon, Text } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { RankingItem } from './types';

const TopbarHeading: React.FC<{
  title: string;
  titleAcronym?: string;
}> = ({ title, titleAcronym }) => (
  <Heading
    as="h3"
    color="gray.800"
    fontSize={{ base: 'md', md: 'lg', xl: 'xl' }}
    fontWeight="bold"
    mr={{ sm: 2, lg: 6 }}
    isTruncated
    height={8}
    display="flex"
    alignItems="center"
  >
    <Text
      as="span"
      display={
        titleAcronym
          ? {
              base: 'none',
              md: 'inline-block',
            }
          : undefined
      }
    >
      {title}
    </Text>
    {titleAcronym && (
      <Text
        as="span"
        display={{
          base: 'none',
          sm: 'inline-block',
          md: 'none',
        }}
      >
        {titleAcronym}
      </Text>
    )}
  </Heading>
);

const TopbarLogo = () => {
  const { institution } = useInstitution();
  return (
    <InstitutionLogo
      src={institution.logo}
      alt={`${institution.name} logo`}
      objectFit="contain"
      width="210px"
      height="70px"
      display={{ base: 'none', xl: 'block' }}
    />
  );
};

const OverallRankDisplay = React.memo(({ text, rank, year }: RankingItem) => (
  <>
    <HStack>
      <Box position="relative">
        {/* create an icon - ten-pointed star */}
        {[...Array(10)].map((el, index) => (
          <Icon
            key={index}
            position="absolute"
            boxSize={4}
            transform={`rotate(${32 + 36 * index}deg)`}
          >
            <CaretSortUp20 color="#FCCF35" />
          </Icon>
        ))}
        <CheckmarkFilled16 color="#FCCF35" />
      </Box>
      <Flex ml={2} fontWeight="bold">
        <Text>
          #{rank} in {text} {year}
        </Text>
      </Flex>
    </HStack>
  </>
));

OverallRankDisplay.displayName = 'OverallRankDisplay';

const Topbar = ({
  children,
  fitSidebar = false,
  withLogo = false,
  title,
  titleAcronym,
  dropdownRef,
  wurRank,
  wurYear,
  darkVariant = false,
  impactYear,
  impactRank,
  ...props
}: React.PropsWithChildren<
  {
    impactYear?: number;
    impactRank?: string;
    title?: string;
    titleAcronym?: string;
    fitSidebar?: boolean;
    withLogo?: boolean;
    dropdownRef?: React.Ref<HTMLDivElement>;
    wurRank?: string;
    wurYear?: number;
    darkVariant?: boolean;
  } & BoxProps
>) => {
  const rankingData = useMemo<RankingItem[]>(() => {
    const data: RankingItem[] = [];

    if (wurRank && wurYear) {
      data.push({
        rank: wurRank,
        year: wurYear,
        text: 'World University Rankings',
      });
    }

    if (impactRank && impactYear) {
      data.push({
        rank: impactRank,
        year: impactYear,
        text: 'Sustainability Impact Ratings',
      });
    }

    return data;
  }, [wurRank, wurYear, impactRank, impactYear]);

  return (
    <Box>
      <Flex
        w={
          fitSidebar
            ? { base: '100%', lg: `calc(100% - ${SIDEBAR_WIDTH})` }
            : undefined
        }
        /* if this changes the height and height of LegacyIFrame and marginTop of LegacyAppLayout needs to change */
        minH={16}
        left={fitSidebar ? { base: 0, lg: SIDEBAR_WIDTH } : undefined}
        top={fitSidebar ? { base: 16, lg: 0 } : undefined}
        px={6}
        position={fitSidebar ? 'fixed' : undefined}
        zIndex={fitSidebar ? 'sticky' : undefined}
        bg={darkVariant ? 'gray.700' : 'gray.100'}
        flexDir="column"
        {...props}
      >
        <Flex flexDir="row" alignItems="center" flexGrow={3}>
          {withLogo && <TopbarLogo />}
          {title && (
            <Flex direction="column" ml={3}>
              <TopbarHeading title={title} titleAcronym={titleAcronym} />
              {rankingData.length > 0 && (
                <HStack spacing={'10'} mt={1} alignItems="center" h={8}>
                  {rankingData.map((item, index) => (
                    <OverallRankDisplay
                      key={`${item.rank}-${index}`}
                      rank={item.rank}
                      year={item.year}
                      text={item.text}
                    />
                  ))}
                </HStack>
              )}
            </Flex>
          )}
          <Box flexGrow={1}>{children}</Box>
        </Flex>
        {dropdownRef && (
          <Flex flexDir="row">
            <Box flexGrow={1}>
              <Box ref={dropdownRef} />
            </Box>
          </Flex>
        )}
      </Flex>
      {dropdownRef && <Box zIndex={3000} ref={dropdownRef} />}
    </Box>
  );
};

export default Topbar;
